import type { DataLoadStatus, IndicatorSeries } from "../types";
import type { HousingPolicyBundle } from "./housingPolicyRegimes";
import type { LegacyMortgageStressBundle } from "./legacyMortgageStress";
import { latestChange, latestValue, unitLabel } from "../utils/series.ts";

export type NarrativeTone = "tightening" | "easing" | "mixed" | "neutral" | "na";

export interface TabNarrative {
  id: string;
  title: string;
  conclusion: string;
  importance: string;
  watch: string;
  evidence: string[];
  tone: NarrativeTone;
  caveat?: string;
}

interface BuildTabNarrativesInput {
  bok: IndicatorSeries;
  ktb3: IndicatorSeries;
  curve103: IndicatorSeries;
  policyGap: IndicatorSeries;
  usdKrw: IndicatorSeries;
  krUs2Spread: IndicatorSeries;
  krUs3Spread: IndicatorSeries;
  bokFedSpread: IndicatorSeries;
  fundingNew: IndicatorSeries;
  fundingStock: IndicatorSeries;
  fundingMomentum: IndicatorSeries;
  mortgageLoan: IndicatorSeries;
  corpLoan: IndicatorSeries;
  mortgageFundingSpread: IndicatorSeries;
  corpLoanFundingSpread: IndicatorSeries;
  creditSpread: IndicatorSeries;
  economicSentiment: IndicatorSeries;
  industrialProduction: IndicatorSeries;
  unsoldTotal: IndicatorSeries;
  housing: HousingPolicyBundle;
  legacyMortgage: LegacyMortgageStressBundle;
  officialHousingPrices: IndicatorSeries[];
  status: DataLoadStatus;
}

function signed(value: number, digits = 1) {
  return `${value > 0 ? "+" : ""}${value.toFixed(digits)}`;
}

function formatValue(series: IndicatorSeries, value: number, digits = 2) {
  const unit = unitLabel(series.unit);
  if (series.unit === "count" || series.unit === "housing_units") return `${Math.round(value).toLocaleString()}${unit}`;
  if (series.unit === "index") return `${value.toFixed(digits)} 지수`;
  return `${value.toFixed(digits)}${unit}`;
}

function latestDate(series: IndicatorSeries) {
  return [...series.observations].reverse().find((row) => row.value != null)?.date ?? null;
}

function evidence(series: IndicatorSeries, label: string, digits = 2) {
  const value = latestValue(series);
  if (value == null) return `${label} N/A`;
  const date = latestDate(series);
  return `${label} ${formatValue(series, value, digits)}${date ? ` · ${date}` : ""}`;
}

function recentObservedDelta(series: IndicatorSeries) {
  const rows = series.observations.filter((row) => row.value != null);
  if (rows.length < 2) return null;
  const lookback = series.source.frequency === "daily" ? 20 : series.source.frequency === "weekly" ? 4 : 1;
  const latest = rows.at(-1)!;
  const prior = rows[Math.max(0, rows.length - 1 - lookback)];
  return {
    value: (latest.value as number) - (prior.value as number),
    from: prior.date,
    to: latest.date
  };
}

function directionPhrase(value: number | null, up: string, down: string, flat: string) {
  if (value == null) return null;
  if (value > 0) return up;
  if (value < 0) return down;
  return flat;
}

function scoreTone(score: number, available: number): NarrativeTone {
  if (available === 0) return "na";
  if (score >= 2) return "tightening";
  if (score <= -2) return "easing";
  if (score === 0) return "mixed";
  return "neutral";
}

function capitalTransactionSnapshot(housing: HousingPolicyBundle) {
  const rtms = housing.transactionModule.rtms;
  const rows = rtms?.scopes.capital ?? [];
  if (!rows.length) return { month: null, total: null, mom: null };
  const months = [...new Set(rows.map((row) => row.month))].sort();
  const configured = rtms?.coverage.latestFullCalendarMonth ?? null;
  const month = configured && months.includes(configured) ? configured : months.at(-1) ?? null;
  if (!month) return { month: null, total: null, mom: null };
  const index = months.indexOf(month);
  const previous = index > 0 ? months[index - 1] : null;
  const totalFor = (target: string | null) => target == null
    ? null
    : rows.filter((row) => row.month === target).reduce((sum, row) => sum + row.transactionCount, 0);
  const total = totalFor(month);
  const priorTotal = totalFor(previous);
  const mom = total != null && priorTotal != null && priorTotal > 0 ? (total / priorTotal - 1) * 100 : null;
  return { month, total, mom };
}

function capitalHousingPrice(series: IndicatorSeries[]) {
  return series.find((item) => item.name.includes("수도권") && item.name.includes("아파트"))
    ?? series.find((item) => item.shortName.includes("수도권"))
    ?? null;
}

function ratesNarrative(input: BuildTabNarrativesInput): TabNarrative {
  const gap = latestValue(input.policyGap);
  const curve = latestValue(input.curve103);
  const marketMove = recentObservedDelta(input.ktb3)?.value ?? null;
  if (gap == null) {
    return {
      id: "rates",
      title: "정책금리와 시장금리, 이렇게 읽으면 됩니다",
      conclusion: "정책금리와 국고채 3년을 함께 비교할 데이터가 부족해 현재 방향을 단정하지 않습니다.",
      importance: "시장금리는 기준금리보다 먼저 움직일 수 있어, 실제 금융여건이 정책보다 앞서 완화·긴축되는지 확인하는 첫 단계입니다.",
      watch: "국고채 3년과 기준금리가 같은 날짜축에서 다시 연결되는지 먼저 확인합니다.",
      evidence: [evidence(input.bok, "기준금리"), evidence(input.ktb3, "국고채 3년")],
      tone: "na"
    };
  }
  const relation = gap > 0
    ? `국고채 3년이 기준금리보다 ${Math.abs(gap).toFixed(1)}bp 높아, 시장의 중기 금리환경이 현재 정책금리보다 위에 있습니다.`
    : gap < 0
      ? `국고채 3년이 기준금리보다 ${Math.abs(gap).toFixed(1)}bp 낮아, 시장의 중기 금리환경이 현재 정책금리보다 아래에 있습니다.`
      : "국고채 3년과 기준금리가 거의 같은 수준입니다.";
  const move = directionPhrase(
    marketMove,
    " 최근 약 한 달의 국고채 3년은 위로 움직였습니다.",
    " 최근 약 한 달의 국고채 3년은 아래로 움직였습니다.",
    " 최근 약 한 달의 국고채 3년 방향은 거의 변하지 않았습니다."
  ) ?? "";
  const curveText = curve == null
    ? "10년-3년 기울기는 현재 확인할 수 없습니다."
    : curve >= 0
      ? "10년 금리가 3년보다 높은 상태인지 그 폭이 확대·축소되는지도 함께 봅니다."
      : "10년-3년 구간은 역전 상태라, 신용·대출·실물로 실제 압박이 이어지는지 함께 확인합니다.";
  return {
    id: "rates",
    title: "정책금리와 시장금리, 이렇게 읽으면 됩니다",
    conclusion: `${relation}${move}`,
    importance: "기준금리는 정책의 출발점이고 시장금리는 실제 조달비용의 선행 신호입니다. 둘의 간격이 벌어지는 방향이 이후 은행 조달·대출금리로 전달되는지가 핵심입니다.",
    watch: `${gap > 0 ? "국고채 3년이 기준금리에 가까워지며 간격이 줄어드는지" : gap < 0 ? "낮아진 시장금리가 실제 조달·대출금리 하락으로 이어지는지" : "시장금리가 어느 쪽으로 먼저 이탈하는지"} 확인합니다. ${curveText}`,
    evidence: [evidence(input.bok, "기준금리"), evidence(input.ktb3, "국고채 3년"), evidence(input.policyGap, "정책 기대차", 1), evidence(input.curve103, "10Y-3Y", 1)],
    tone: gap > 0 ? (marketMove != null && marketMove < 0 ? "mixed" : "tightening") : gap < 0 ? (marketMove != null && marketMove > 0 ? "mixed" : "easing") : "neutral",
    caveat: "정책 기대차는 향후 기준금리 결정을 예언하는 값이 아니라 현재 시장가격과 정책금리의 관찰 차이입니다."
  };
}

function globalNarrative(input: BuildTabNarrativesInput): TabNarrative {
  const spread = latestValue(input.krUs2Spread);
  const fx = latestValue(input.usdKrw);
  const fxMove = recentObservedDelta(input.usdKrw)?.value ?? null;
  const spreadText = spread == null
    ? "한미 2년 금리차를 현재 계산할 수 없습니다."
    : spread < 0
      ? `한국 2년 금리가 미국보다 ${Math.abs(spread).toFixed(1)}bp 낮습니다.`
      : spread > 0
        ? `한국 2년 금리가 미국보다 ${spread.toFixed(1)}bp 높습니다.`
        : "한미 2년 금리가 거의 같은 수준입니다.";
  const fxText = fx == null
    ? " 원/달러 환율은 현재 연결되지 않았습니다."
    : ` 원/달러는 ${Math.round(fx).toLocaleString()}원이며${fxMove == null || fxMove === 0 ? " 최근 방향은 뚜렷하지 않습니다." : fxMove > 0 ? " 최근 약 한 달 기준으로 상승했습니다." : " 최근 약 한 달 기준으로 하락했습니다."}`;
  return {
    id: "global",
    title: "한미 금리차와 환율, 이렇게 읽으면 됩니다",
    conclusion: `${spreadText}${fxText} 상대금리와 환율은 함께 보되 금리차 하나로 환율을 설명하지 않습니다.`,
    importance: "한미 상대금리는 원화·달러 자금의 기회비용을 보여주는 한 축입니다. 실제 환율이 같은 방향으로 움직이는지 확인해야 국내 금융여건에 들어오는 외부 압력을 구분할 수 있습니다.",
    watch: "한미 2·3년 금리차가 좁아지거나 벌어질 때 원/달러가 실제로 같은 방향으로 반응하는지, 반대로 움직이면 어떤 다른 충격이 작동하는지 확인합니다.",
    evidence: [evidence(input.krUs2Spread, "KR-US 2Y", 1), evidence(input.krUs3Spread, "KR-US 3Y", 1), evidence(input.bokFedSpread, "BOK-Fed", 1), evidence(input.usdKrw, "USD/KRW", 0)],
    tone: spread == null && fx == null ? "na" : "mixed",
    caveat: "환율은 금리차 외에도 위험선호, 무역·자본흐름, 정책 기대 등 여러 요인의 영향을 받습니다."
  };
}

function creditNarrative(input: BuildTabNarrativesInput): TabNarrative {
  const fundingGap = latestValue(input.fundingMomentum);
  const creditMove = recentObservedDelta(input.creditSpread)?.value ?? null;
  const mortgageMove = recentObservedDelta(input.mortgageLoan)?.value ?? null;
  const parts: string[] = [];
  let score = 0;
  let available = 0;
  if (fundingGap != null) {
    available += 1;
    if (fundingGap > 0) { score += 1; parts.push(`은행의 새 금융채 조달비용이 기존 평균보다 ${fundingGap.toFixed(1)}bp 높습니다.`); }
    else if (fundingGap < 0) { score -= 1; parts.push(`은행의 새 금융채 조달비용이 기존 평균보다 ${Math.abs(fundingGap).toFixed(1)}bp 낮습니다.`); }
    else parts.push("은행의 신규·기존 금융채 조달비용이 비슷합니다.");
  }
  if (creditMove != null) {
    available += 1;
    if (creditMove > 0) { score += 1; parts.push(`기업 신용스프레드는 최근 약 한 달 ${creditMove.toFixed(1)}bp 넓어졌습니다.`); }
    else if (creditMove < 0) { score -= 1; parts.push(`기업 신용스프레드는 최근 약 한 달 ${Math.abs(creditMove).toFixed(1)}bp 좁아졌습니다.`); }
    else parts.push("기업 신용스프레드의 최근 방향은 거의 변하지 않았습니다.");
  }
  if (mortgageMove != null) {
    available += 1;
    if (mortgageMove > 0) score += 1;
    if (mortgageMove < 0) score -= 1;
  }
  return {
    id: "credit",
    title: "조달비용이 실제 대출금리로 가는 과정",
    conclusion: parts.length ? parts.join(" ") : "조달·신용의 현재 방향을 설명할 데이터가 부족합니다.",
    importance: "시장금리 변화가 은행의 새 조달비용을 거쳐 기업·가계의 실제 신규 대출금리까지 전달되는지를 보는 탭입니다. 중간 단계가 끊기면 시장금리 변화가 곧바로 차주 부담 변화라는 뜻은 아닙니다.",
    watch: "신규 금융채 금리가 먼저 움직인 뒤 기업·주담대 신규금리가 같은 방향으로 따라가는지, 동시에 신용스프레드까지 확대·축소되는지 확인합니다.",
    evidence: [evidence(input.fundingNew, "신규 조달"), evidence(input.fundingStock, "잔액 조달"), evidence(input.mortgageLoan, "신규 주담대"), evidence(input.creditSpread, "AA- 신용스프레드", 1)],
    tone: scoreTone(score, available),
    caveat: "대출금리-조달금리 차이는 상품구성·신용위험·운영비·경쟁이 함께 들어간 관찰값이며 은행 순마진과 동일하지 않습니다."
  };
}

function housingNarrative(input: BuildTabNarrativesInput): TabNarrative {
  const newMortgage = latestValue(input.legacyMortgage.inputs.newMortgageRate);
  const existingMortgage = latestValue(input.legacyMortgage.inputs.existingMortgageCost);
  const lockIn = latestValue(input.legacyMortgage.lockInSpread);
  const outlookMove = recentObservedDelta(input.legacyMortgage.inputs.housingPriceOutlook)?.value ?? null;
  const transactions = capitalTransactionSnapshot(input.housing);
  const capitalPrice = capitalHousingPrice(input.officialHousingPrices);
  const priceMove = capitalPrice ? latestChange(capitalPrice) : null;
  const parts: string[] = [];
  if (newMortgage != null && existingMortgage != null && lockIn != null) {
    parts.push(lockIn > 0
      ? `신규 주담대금리가 기존 잔액금리보다 ${Math.abs(lockIn).toFixed(2)}%p 높아 새 차주의 금융비용이 더 높은 상태입니다.`
      : lockIn < 0
        ? `신규 주담대금리가 기존 잔액금리보다 ${Math.abs(lockIn).toFixed(2)}%p 낮아 신규 금융비용은 기존 평균보다 낮은 상태입니다.`
        : "신규·기존 주담대 금리차가 거의 없습니다.");
  }
  if (transactions.total != null) {
    parts.push(`수도권 실거래는 ${transactions.month ?? "최근월"} ${transactions.total.toLocaleString()}건${transactions.mom == null ? "" : `, 직전 수집완료월 대비 ${signed(transactions.mom, 1)}%`}입니다.`);
  }
  if (capitalPrice && priceMove != null) {
    parts.push(`공식 수도권 아파트 가격지수는 최근 관측에서 ${priceMove > 0 ? "상승" : priceMove < 0 ? "하락" : "보합"}했습니다.`);
  }
  const watchSignals: string[] = [];
  if (lockIn != null) watchSignals.push("신규-기존 주담대 금리차가 줄어드는지");
  if (transactions.total != null) watchSignals.push("실거래량이 같은 방향으로 반응하는지");
  if (capitalPrice) watchSignals.push("거래 변화 뒤 공식 가격지수까지 이어지는지");
  if (outlookMove != null) watchSignals.push("주택가격 기대가 실제 거래·가격과 같은 방향인지");
  return {
    id: "housing",
    title: "금리 충격이 주택시장까지 갔는지 보기",
    conclusion: parts.length ? parts.join(" ") : "주담대·실거래·가격을 함께 설명할 데이터가 아직 충분하지 않습니다.",
    importance: "금리가 오른다고 주택가격이 즉시 움직이는 것은 아닙니다. 신규 대출비용 → 기존차주 부담 → 거래량 → 가격의 순서를 분리해 실제 전이 여부를 확인합니다.",
    watch: `${watchSignals.length ? watchSignals.join(" → ") : "주담대 금리, 거래량, 가격지수가 같은 방향으로 연결되는지"} 순서로 확인합니다.`,
    evidence: [
      evidence(input.legacyMortgage.inputs.newMortgageRate, "신규 주담대"),
      evidence(input.legacyMortgage.inputs.existingMortgageCost, "기존 주담대"),
      evidence(input.legacyMortgage.lockInSpread, "신규-기존 gap"),
      transactions.total == null ? "수도권 거래 N/A" : `수도권 거래 ${transactions.total.toLocaleString()}건 · ${transactions.month}`
    ],
    tone: lockIn == null ? "na" : (transactions.mom != null && ((lockIn > 0 && transactions.mom < 0) || (lockIn < 0 && transactions.mom > 0))) ? (lockIn > 0 ? "tightening" : "easing") : "mixed",
    caveat: input.housing.transactionModule.status === "backfill" || input.housing.transactionModule.status === "partial"
      ? `RTMS MAX 과거자료는 아직 backfill 중입니다. 현재 수집완료 구간의 관측과 전체 역사 완성도를 구분해 봅니다.`
      : "실거래·가격의 동행이나 시차가 관측돼도 금리만으로 인과를 확정하지 않습니다."
  };
}

function realNarrative(input: BuildTabNarrativesInput): TabNarrative {
  const esiMove = recentObservedDelta(input.economicSentiment)?.value ?? null;
  const productionMove = recentObservedDelta(input.industrialProduction)?.value ?? null;
  const unsoldMove = recentObservedDelta(input.unsoldTotal)?.value ?? null;
  const descriptions: string[] = [];
  let score = 0;
  let available = 0;
  if (esiMove != null) {
    available += 1;
    if (esiMove > 0) { score -= 1; descriptions.push(`경제심리는 직전월보다 ${Math.abs(esiMove).toFixed(1)} 올라 개선 방향입니다.`); }
    else if (esiMove < 0) { score += 1; descriptions.push(`경제심리는 직전월보다 ${Math.abs(esiMove).toFixed(1)} 내려 약화 방향입니다.`); }
    else descriptions.push("경제심리는 직전월과 비슷합니다.");
  }
  if (productionMove != null) {
    available += 1;
    if (productionMove > 0) { score -= 1; descriptions.push("생산활동은 직전월보다 개선됐습니다."); }
    else if (productionMove < 0) { score += 1; descriptions.push("생산활동은 직전월보다 약해졌습니다."); }
    else descriptions.push("생산활동은 직전월과 비슷합니다.");
  }
  if (unsoldMove != null) {
    available += 1;
    if (unsoldMove > 0) { score += 1; descriptions.push("미분양은 늘어 주택 재고부담이 커지는 방향입니다."); }
    else if (unsoldMove < 0) { score -= 1; descriptions.push("미분양은 줄어 주택 재고부담이 완화되는 방향입니다."); }
    else descriptions.push("미분양은 직전월과 비슷합니다.");
  }
  return {
    id: "real",
    title: "금융여건 변화가 실제경제에 닿았는지 확인",
    conclusion: descriptions.length ? descriptions.join(" ") : "실물경제의 최근 방향을 설명할 데이터가 부족합니다.",
    importance: "금리·신용시장의 변화가 실제 경기 충격인지 판단하려면 심리만이 아니라 생산과 주택 재고 같은 후행 지표가 함께 움직이는지 확인해야 합니다.",
    watch: "경제심리와 생산이 여러 달 같은 방향으로 움직이는지, 미분양 변화가 주택 거래·가격 신호와 일치하는지 확인합니다.",
    evidence: [evidence(input.economicSentiment, "경제심리", 1), evidence(input.industrialProduction, "전산업생산", 1), evidence(input.unsoldTotal, "미분양", 0)],
    tone: scoreTone(score, available),
    caveat: "한 달 변화만으로 경기국면을 확정하지 않고 여러 지표가 같은 방향으로 이어지는지를 봅니다."
  };
}

function sourcesNarrative(input: BuildTabNarrativesInput): TabNarrative {
  const errors = input.status.issues.filter((issue) => issue.severity === "error").length;
  const warnings = input.status.issues.filter((issue) => issue.severity === "warning").length;
  const complete = input.status.loadedSeries >= input.status.expectedSeries && errors === 0;
  const conclusion = complete
    ? "핵심 계열은 모두 연결되어 있습니다. 남은 표시는 주로 backfill 진행상태·지연·의도적 N/A를 구분하기 위한 진단 정보입니다."
    : errors > 0
      ? `일부 데이터 연결에 실제 오류가 있습니다. 오류 ${errors}건은 해석 전에 먼저 확인해야 합니다.`
      : "일부 계열이 아직 수집·검증 중입니다. 화면의 N/A가 곧 데이터 수집 실패를 뜻하지는 않습니다.";
  return {
    id: "sources",
    title: "데이터 상태는 ‘쓸 수 있나’부터 읽습니다",
    conclusion,
    importance: "이 모니터는 공식 동일정의 계열이 없거나 검증이 끝나지 않으면 숫자를 임의로 채우지 않습니다. 따라서 N/A·지연·backfill을 실패와 구분하는 것이 해석의 일부입니다.",
    watch: "오류가 생기면 해당 계열을 해석에서 제외하고, warning은 stale·부분수집·역사 backfill 여부를 확인합니다. 수집이 회복되면 기존 checkpoint를 이어갑니다.",
    evidence: [
      `연결 ${input.status.loadedSeries}/${input.status.expectedSeries}`,
      `오류 ${errors}건`,
      `경고 ${warnings}건`,
      input.status.collectedAt ? `최근 수집 ${input.status.collectedAt}` : "최근 수집시각 N/A"
    ],
    tone: errors > 0 ? "tightening" : complete ? "neutral" : "mixed"
  };
}

export function buildTabNarratives(input: BuildTabNarrativesInput) {
  return {
    rates: ratesNarrative(input),
    global: globalNarrative(input),
    credit: creditNarrative(input),
    housing: housingNarrative(input),
    real: realNarrative(input),
    sources: sourcesNarrative(input)
  };
}
